import { useEffect, useRef, useState } from "react";
import { getConversationHistory } from "@/api/sdk.gen";
import { appState } from "@/app/appState";
import { formatHistoryTime } from "@/components/richtext/Citation/CitationNode";
import { TextMessageDisplay } from "../chatMessage";

type HistoryRow = {
    id: number;
    time: string;
    epoch: number;
    role: "user" | "assistant";
    kind: string;
    text: string;
    activity: string;
    cited: boolean;
};

const QUOTE_HIGHLIGHT = "history-cite";

/**
 * Range covering `quote` in the rendered text under `root`. Whitespace is ignored on both sides,
 * since rendered markdown drops the line breaks and indentation the quote was taken from.
 */
export function quoteRange(root: Node, quote: string): Range | null {
    const needle = quote.replace(/\s+/g, "");
    if (!needle) return null;
    const chars: { node: Text; offset: number }[] = [];
    const flat: string[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
        for (let i = 0; i < node.data.length; i++) {
            if (/\s/.test(node.data[i])) continue;
            flat.push(node.data[i]);
            chars.push({ node, offset: i });
        }
    }
    const at = flat.join("").indexOf(needle);
    if (at < 0) return null;
    const first = chars[at];
    const last = chars[at + needle.length - 1];
    const range = document.createRange();
    range.setStart(first.node, first.offset);
    range.setEnd(last.node, last.offset + 1);
    return range;
}

/** Compressed history of a conversation; with a time window, scrolls to and marks the cited rows. */
export const HistoryViewer = ({ conversationId, startTime, endTime, citedText }: {
    conversationId: number;
    startTime?: number;
    endTime?: number;
    citedText?: string;
}) => {
    const [rows, setRows] = useState<HistoryRow[] | null>(null);
    const [failed, setFailed] = useState(false);
    const listRef = useRef<HTMLDivElement | null>(null);
    const firstCitedRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let live = true;
        getConversationHistory({ body: { conversation_id: conversationId, start_time: startTime, end_time: endTime } })
            .then((response) => {
                if (!live) return;
                if (response.error) setFailed(true);
                else setRows(((response.data as { rows?: HistoryRow[] })?.rows) ?? []);
            })
            .catch(() => live && setFailed(true));
        return () => { live = false; };
    }, [conversationId, startTime, endTime]);

    // The rows' editors fill in after mount, so scroll and mark once they have rendered.
    useEffect(() => {
        if (!rows) return;
        const frame = requestAnimationFrame(() => {
            firstCitedRef.current?.scrollIntoView({ block: "center" });
            if (!citedText || typeof CSS === "undefined" || !CSS.highlights) return;
            const ranges = Array.from(listRef.current?.querySelectorAll("[data-cited-text]") ?? [])
                .map((el) => quoteRange(el, citedText))
                .filter((range): range is Range => range !== null);
            if (ranges.length > 0) CSS.highlights.set(QUOTE_HIGHLIGHT, new Highlight(...ranges));
        });
        return () => {
            cancelAnimationFrame(frame);
            if (typeof CSS !== "undefined" && CSS.highlights) CSS.highlights.delete(QUOTE_HIGHLIGHT);
        };
    }, [rows, citedText]);

    if (failed) return <p>{appState.t("chat.history.failed")}</p>;
    if (rows === null) return <p className="opacity-60">{appState.t("chat.history.loading")}</p>;
    if (rows.length === 0) return <p className="opacity-60">{appState.t("chat.history.empty")}</p>;

    let firstCitedSeen = false;
    return (
        <div ref={listRef} className="flex flex-col gap-2 max-h-[70vh] overflow-y-auto text-left">
            {rows.map((row) => {
                const isFirstCited = row.cited && !firstCitedSeen;
                if (isFirstCited) firstCitedSeen = true;
                return (
                    <div
                        key={row.id}
                        ref={isFirstCited ? firstCitedRef : undefined}
                        className={`rounded px-2 py-1 ${row.cited ? "history-row-cited" : ""}`}
                    >
                        <div className="text-xs opacity-60">
                            {row.role === "user" ? appState.t("chat.history.roleUser") : appState.t("chat.history.roleAssistant")}
                            {" · "}
                            {formatHistoryTime(row.epoch)}
                        </div>
                        {row.text && (
                            <div data-cited-text={row.cited ? "" : undefined}>
                                <TextMessageDisplay contentClass="" content={row.text} />
                            </div>
                        )}
                        {row.activity && <div className="whitespace-pre-wrap text-xs font-mono opacity-70">{row.activity}</div>}
                    </div>
                );
            })}
        </div>
    );
};

export const openHistoryViewer = (params: { conversationId: number; startTime?: number; endTime?: number; citedText?: string }) => {
    appState.setModal({
        title: appState.t("chat.history.title"),
        body: <HistoryViewer {...params} />,
    });
};
